import * as React from 'react';

import {useSenseAi} from 'src/hooks/useSenseAi';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {SenseAiIntroPanel} from 'src/components/sense-ai/ai-intro-panel';

import css from './sense-ai.css';


type ClassNames = Readonly<{
  aiPanel?: string;
}>;

export type SenseAiProps = {
  children?: React.ReactNode;
  defaultIntroOpen?: boolean;
  heading?: React.ReactNode;
  description?: React.ReactNode;
  confirmButtonText?: string;
  dismissButtonText?: string;
  onConfirmButtonClick?:
    | ((e?: React.SyntheticEvent<HTMLElement>) => unknown)
    | null
    | undefined;
  onDismissButtonClick?:
    | ((e?: React.SyntheticEvent<HTMLElement>) => unknown)
    | null
    | undefined;
  onTrackEvent?: (
    event: 'Content Generated' | 'Content Generator Visited',
  ) => unknown;
  url?: string;
  classNames?: ClassNames;
  onAiPanelOpen?:
    | ((e?: React.SyntheticEvent<HTMLElement>, isOpen?: boolean) => unknown)
    | null
    | undefined;
  showIntro?: boolean;
};

export const SenseAi = ({
  children,
  defaultIntroOpen = false,
  heading,
  description,
  confirmButtonText,
  onConfirmButtonClick,
  onDismissButtonClick,
  classNames,
  onAiPanelOpen,
  showIntro = true,
  onTrackEvent,
}: SenseAiProps) => {
  const {
    state: {showAiPanel, showCustomComponent},
    setShowAiPanel,
    setShowCustomComponent,
  } = useSenseAi();

  const shouldShowIntro = showIntro && showAiPanel;

  const onConfirm = (e: React.SyntheticEvent<HTMLElement>) => {
    showIntro && onTrackEvent && onTrackEvent('Content Generated');
    setShowCustomComponent(true);
    setShowAiPanel(false);
    onConfirmButtonClick?.(e);
    // TODO(Nishant): This is because the toast incorrectly
    // calls onDismiss when we transition to custom component/children screen
    onAiPanelOpen?.(e, true);
  };

  const onDismiss = (e: undefined) => {
    setShowCustomComponent(false);
    setShowAiPanel(false);
    onDismissButtonClick?.(e);
    onAiPanelOpen?.(e, false);
  };

  const handleAIButtonClick = (e: React.SyntheticEvent<HTMLElement>) => {
    onTrackEvent && onTrackEvent('Content Generator Visited');
    if (showIntro) {
      const show = !showAiPanel;
      setShowAiPanel(show);
      onAiPanelOpen?.(e, true);
    } else {
      onConfirm(e);
    }
  };

  //NOTE:(diwakersurya) this is considered only for first time
  React.useEffect(() => {
    setShowAiPanel(defaultIntroOpen);
    if (defaultIntroOpen) {
      setShowCustomComponent(false);
    }
  }, []);

  const disableAiButton = showIntro
    ? showCustomComponent || showAiPanel
    : showCustomComponent;

  return (
    <div className={css.aiContainer}>
      <div className={classNames?.aiPanel}>
        {showCustomComponent && children}
        {shouldShowIntro && (
          <SenseAiIntroPanel
            heading={heading}
            description={description}
            confirmButtonText={confirmButtonText}
            onConfirmButtonClick={onConfirm}
            // @ts-expect-error - TS2322 - Type '(e: undefined) => void' is not assignable to type '() => void'.
            onDismissButtonClick={onDismiss}
          />
        )}
      </div>
      <Button
        size="small"
        type="secondary"
        iconLeftName="sparkles"
        iconLeftType="solid"
        disabled={disableAiButton}
        onClick={handleAIButtonClick}
      />
    </div>
  );
};
